import { Component, ElementRef, OnInit, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { OrderAnalyticsConfigService } from '../../../services/order-analytics-config/order-analytics-config.service';
import { Subject, takeUntil } from 'rxjs';
import { StandardStatisticsData } from '../../../models/statistics';
import { OrderAnalyticsProductListConfigService } from '../../../services/order-analytics-product-list-config.service';

@Component({
    selector: 'lib-order-analytics-standalone',
    templateUrl: './order-analytics-standalone.component.html',
    styleUrls: ['./order-analytics-standalone.component.css'],
    standalone: false
})
export class OrderAnalyticsStandaloneComponent implements OnInit, OnDestroy {
  data: StandardStatisticsData[];
  protected unsubscribe: Subject<void> = new Subject();
  selectedGraph: OrderAnalyticsGraphList;
  graphList: OrderAnalyticsGraphList[] = [
    {
      id: '1',
      translationKey: 'YEAR',
      groupBy: 'orderCreationDateYear',
      heading: 'YEAR',
      headerRows: ['YEAR', 'SALES_AMOUNT', 'QTY', 'NUMBER_OF_ORDERS'],
      hideVerticalGridLines: true
    },
    {
      id: '2',
      translationKey: 'MONTH',
      groupBy: 'orderCreationDateYearMonth',
      heading: 'MONTH',
      headerRows: ['MONTH', 'SALES_AMOUNT', 'QTY', 'NUMBER_OF_ORDERS'],
      hideVerticalGridLines: true
    },
    {
      id: '3',
      translationKey: 'PRODUCT_CATEGORIES_LAST_12_MONTHS',
      groupBy: this.orderAnalyticsConfigService.useCategoryDescription
        ? 'productCategoryDescription'
        : 'productCategoryCode',
      heading: 'PRODUCT_CATEGORY',
      headerRows: ['PRODUCT_CATEGORY', 'SALES_AMOUNT', 'QTY'],
      hideVerticalGridLines: true
    }
  ];

  // The selected period's amount, shown as the big figure above the chart. Reset per tab (each
  // graph has its own grouping) - lib-order-analytics-chart-table-horizontal re-emits it as soon as
  // it picks a default (the most recent period) for the newly selected tab.
  selectedKey: string;
  selectedAmount = 0;

  @ViewChildren('tabButton') tabButtons: QueryList<ElementRef<HTMLButtonElement>>;

  constructor(
    protected orderAnalyticsConfigService: OrderAnalyticsConfigService,
    public orderAnalyticsProductListConfigService: OrderAnalyticsProductListConfigService
  ) { }

  ngOnInit(): void {
    this.orderAnalyticsConfigService
      .getItems()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => {
        this.data = data;
        if (this.orderAnalyticsConfigService.useDefaultGraphList) {
          this.graphList = this.orderAnalyticsConfigService.defaultGraphList;
        }
        if (this.graphList && this.graphList.length > 0) {
          const graphListItem = this.graphList[0];
          this.changeGraph(graphListItem.id);
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  changeGraph(id: string) {
    const foundGraph = this.graphList.find(g => g.id === id);
    if (!!foundGraph) {
      this.selectedGraph = foundGraph;
      // A new tab regroups by a different field entirely (year vs. month vs. category) - last
      // tab's selected key would not mean anything here, so the child picks its own default again.
      this.selectedKey = undefined;
      this.selectedAmount = 0;
    }
  }

  onTabKeydown(event: KeyboardEvent, index: number): void {
    const count = this.graphList.length;
    let nextIndex: number | null = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (index + 1) % count;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (index - 1 + count) % count;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = count - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const target = this.graphList[nextIndex];
    this.changeGraph(target.id);
    // Roving tabindex: focus follows the newly active tab so arrow navigation stays usable with a
    // screen reader or keyboard-only, instead of leaving focus behind on the previous button.
    queueMicrotask(() => this.tabButtons?.get(nextIndex)?.nativeElement.focus());
  }
}

export interface OrderAnalyticsGraphList {
  id: string
  translationKey: string,
  groupBy: string,
  heading: string,
  headerRows: string[],
  hideVerticalGridLines?: boolean
  showProductAnalyticsList?: boolean
}
